import { makeAutoObservable, runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import { createContext, ReactNode, useContext, useEffect } from 'react';
import {
    ChannelPermissionResponse,
    ChannelResponse,
} from '../../../server/response/responseSchema';
import { ChannelType } from '../../../types/channelTypes';
import firebaseStore from '../../stores/firebaseStore';
import { apiClient } from '../../clientUtils/apiClient';
import { useMobxInstance } from '../../clientUtils/mobxUtil';

/**
 * 채널 정보 관리
 * - 채널 스토어는 채널 매니저 스토어에서 인스턴스를 관리함
 */
class ChannelStore {
    readonly channelType: ChannelType;

    channel: ChannelResponse | null = null;
    channelPermission: ChannelPermissionResponse | null = null;

    // 초기 상태에서는 일단 로딩 중으로 표시
    isLoading = true;
    isPermissionLoading = true;

    constructor(channelType: ChannelType) {
        makeAutoObservable(this);
        this.channelType = channelType;
    }

    /**
     * 글 작성 권한
     */
    get canWrite() {
        return this.channelPermission?.canWrite ?? false;
    }

    /**
     * 댓글 작성 권한
     */
    get canComment() {
        return this.channelPermission?.canComment ?? false;
    }

    /**
     * 채널 기본 정보 조회
     */
    async fetchChannel() {
        this.isLoading = true;

        try {
            const result = await apiClient.channel.public.getChannel.$query({
                channelType: this.channelType,
            });

            runInAction(() => {
                this.channel = result;
            });
        } catch (error) {
            console.error('Failed to fetch channel:', error);
        } finally {
            runInAction(() => {
                this.isLoading = false;
            });
        }
    }

    /**
     * 채널 권한 조회
     */
    async fetchChannelPermission() {
        this.isPermissionLoading = true;

        try {
            if (firebaseStore.me) {
                const result = await apiClient.channel.member.getChannelPermission.$query({
                    channelType: this.channelType,
                });

                runInAction(() => {
                    this.channelPermission = result;
                });
            } else {
                runInAction(() => {
                    this.channelPermission = null;
                });
            }
        } catch (error) {
            console.error('Failed to fetch channel permission:', error);
            runInAction(() => {
                this.channelPermission = null;
            });
        } finally {
            runInAction(() => {
                this.isPermissionLoading = false;
            });
        }
    }
}

const ChannelContext = createContext<ChannelStore | null>(null);

export const ChannelProvider = observer(
    ({ channelType, children }: { channelType: ChannelType; children: ReactNode }) => {
        const channelStore = useMobxInstance(ChannelStore, channelType);

        useEffect(() => {
            channelStore.fetchChannel();
        }, [channelStore]);

        useEffect(() => {
            (async () => {
                // 권한은 firebase 인증 완료 후에 조회해야 함
                await firebaseStore.waitUntilAuthReady();
                channelStore.fetchChannelPermission();
            })();
        }, [channelStore]);

        return <ChannelContext.Provider value={channelStore}>{children}</ChannelContext.Provider>;
    },
);

/**
 * 채널 컨텍스트 사용 훅
 */
export const useChannelStore = (): ChannelStore => {
    const channelStore = useContext(ChannelContext);

    if (!channelStore) {
        throw new Error('useChannelStore must be used within a ChannelProvider');
    }

    return channelStore;
};
